import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import BlendedNominationService from "../Service/BlendedNominationService";
import { useFreezeNomination } from "../Context/FreezeNominationContext";
import { format } from "date-fns";

function CheckBlendedSMENominationStatus() {
  const location = useLocation();
  const { user } = location.state || {}; // Extract user from location.state
  const [errorMsg, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedConnect, setSelectedConnect] = useState(null); // Single selected connect nomination
  const [showTable, setShowTable] = useState(false);
  const [blendedNominations, setBlendedNominations] = useState([]);
  const [reason, setReason] = useState("");
  const [alternativeSME, setAlternativeSME] = useState("");
  const [expandedPrograms, setExpandedPrograms] = useState({});
  const { isFrozen } = useFreezeNomination(); // Get isFrozen state from context

  // Helper function to find the current user's nomination in a connect's nominations list
  const getUserNomination = (connect) => {
    if (!connect.nominations || !user?.email) {
      console.log("No nominations or user email:", { nominations: connect.nominations, userEmail: user?.email });
      return null;
    }
    const userNomination = connect.nominations.find(nomination => nomination.smeEmail === user.email);
    console.log("getUserNomination result:", {
      connectId: connect.connectId,
      userEmail: user.email,
      nominations: connect.nominations,
      userNomination: userNomination
    });
    return userNomination;
  };

  const fetchBlendedNominationsByMonth = useCallback(async () => {
    try {
      const response = await BlendedNominationService.getBlendedSMENominationsByUserAndMonth(
        user,
        selectedMonth
      );
      console.log("Blended nominations response:", response);
      if (response.length !== 0) {
        setBlendedNominations(response);
        setShowTable(true);
      } else {
        setBlendedNominations([]);
        setShowTable(false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setShowTable(false);
    }
  }, [user, selectedMonth]);

  useEffect(() => {
    if (selectedMonth !== "") {
      fetchBlendedNominationsByMonth();
    }
  }, [selectedMonth, fetchBlendedNominationsByMonth]);

  const toggleProgramExpansion = (programId) => {
    setExpandedPrograms(prev => ({
      ...prev,
      [programId]: !prev[programId]
    }));
  };

  const handleConnectSelection = (connect, userNomination, blendedProgramOfferingId, moduleId) => {
    console.log("handleConnectSelection called with:", {
      connect: connect,
      userNomination: userNomination,
      blendedProgramOfferingId: blendedProgramOfferingId,
      moduleId: moduleId
    });
    
    // Store both connect and user nomination info for cancellation
    const selectedConnectData = {
      ...connect,
      userNomination,
      smeNominationId: userNomination?.smeNominationId,
      connectId: connect.connectId,
      blendedProgramOfferingId,
      moduleId
    };
    
    console.log("Setting selectedConnect to:", selectedConnectData);
    setSelectedConnect(selectedConnectData);
    
    const status = userNomination?.status;
    if (status === "NOMINATED") {
      setReason("");
      setAlternativeSME("");
    } else if (status === "CONFIRMED") {
      setReason("");
      setAlternativeSME("");
    }
  };

  const handleReasonChange = (event) => {
    setReason(event.target.value || "");
  };

  const handleAlternativeSMEChange = (event) => {
    setAlternativeSME(event.target.value || "");
  };

  const handleCancelNomination = async () => {
    if (!selectedConnect) {
      alert("Please select a connect nomination to cancel.");
      return;
    }
    if (!reason.trim()) {
      alert("Please provide a reason for cancellation, or enter 'NA'.");
      return;
    }
    if (selectedConnect.userNomination?.status === "CONFIRMED" && !alternativeSME.trim()) {
      alert("Please provide an alternative SME EmailID, or enter 'NA'.");
      return;
    }
    
    try {
      const payload = {
        smeNominationId: selectedConnect.smeNominationId,
        connectId: selectedConnect.connectId,
        blendedProgramOfferingId: selectedConnect.blendedProgramOfferingId,
        moduleId: selectedConnect.moduleId,
        userId: user.userId,
        smeEmail: user.email,
        reason,
        alternativeSMEEmail: alternativeSME,
      };
      
      console.log("Cancellation payload:", payload);
      console.log("Selected connect object:", selectedConnect);
      
      if (!payload.smeNominationId) {
        alert("Error: SME Nomination ID is missing. Cannot proceed with cancellation.");
        return;
      }
      
      const response = await BlendedNominationService.cancelBlendedNomination(payload);
      
      if (response === true) {
        alert("Blended Nomination Cancellation request is sent successfully");
        fetchBlendedNominationsByMonth();
        setSelectedConnect(null);
        setReason("");
        setAlternativeSME("");
      }
    } catch (error) {
      console.error("Error cancelling blended nominations:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to cancel blended nominations";
      setError(errorMessage);
      alert(errorMessage);
    }
  };

  const getWorkingDaysPrior = (startDate, workingDays) => {
    const date = new Date(startDate);
    let count = 0;

    while (count < workingDays) {
      date.setDate(date.getDate() - 1);
      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 6 && dayOfWeek !== 0) {
        count++;
      }
    }
    return date;
  };

  const isConnectDisabled = (connectDate, connect, userNomination) => {
    const currentDate = new Date();
    const fourWorkingDaysPrior = getWorkingDaysPrior(connectDate, 4);
    const status = userNomination?.status || 'NOT_NOMINATED';
    
    // Debug logging for connects with multiple nominations
    if (connect.nominations && connect.nominations.length > 1) {
      console.log("Multiple nominations found for connect:", {
        connectId: connect.connectId,
        connectDate: connectDate,
        totalNominations: connect.nominations.length,
        allNominations: connect.nominations,
        currentUserEmail: user?.email,
        userNomination: userNomination,
        userNominationStatus: status,
        finalizedSME: connect.finalizedSME,
        isDisabled: (
          status === "CANCELLED" ||
          status === "OPTED_OUT" ||
          status === "ASSIGNED_TO_OTHERS" ||
          status === "NOT_NOMINATED" ||
          currentDate >= fourWorkingDaysPrior || 
          isFrozen
        )
      });
    }
    
    return (
      status === "CANCELLED" ||
      status === "OPTED_OUT" ||
      status === "ASSIGNED_TO_OTHERS" ||
      status === "NOT_NOMINATED" ||
      currentDate >= fourWorkingDaysPrior || 
      isFrozen 
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
        Blended Learning Nomination Status
      </h2>
      <div className="flex flex-col items-center mb-4">
        <label className="block text-gray-700 mb-2" htmlFor="month">
          Select Month
        </label>
        <input
          id="month"
          type="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          className="w-60 px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          required
        />
      </div>

      {showTable && blendedNominations.length > 0 && (
        <div className="mt-6 w-full">
          {isFrozen && (
            <div className="bg-red-200 text-red-800 p-4 rounded-lg border border-red-300 mb-4">
              Cancellation requests are frozen, changes are not allowed currently.
            </div>
          )}

          <h3 className="text-xl font-semibold mb-4">Blended Program Nomination Details</h3>
          
          <div className="bg-white rounded-lg shadow-md border border-gray-300 overflow-hidden mb-8">
            <div className="divide-y divide-gray-200">
              {blendedNominations.map((program) => (
                <div key={program.blendedProgramOfferingId} className="bg-white">
                  <div className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => toggleProgramExpansion(program.blendedProgramOfferingId)}
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                      >
                        <svg
                          className={`w-5 h-5 transform transition-transform ${
                            expandedPrograms[program.blendedProgramOfferingId] ? "rotate-90" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                      <div>
                        <h4 className="text-lg font-medium text-gray-800 flex items-center space-x-2">
                          <span>{program.blendedProgramName}</span>
                        </h4>
                        <p className="text-sm text-gray-600">
                          Code: {program.programWDCourseCode} | Track: {program.track} | {program.modules.length} modules
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {expandedPrograms[program.blendedProgramOfferingId] && (
                    <div className="px-6 pb-6">
                      <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                        {program.modules.map((module, moduleIdx) => (
                          <div key={`${module.moduleId}-${moduleIdx}`} className="mb-4 border-b border-gray-200 last:border-b-0 pb-4 last:pb-0">
                            <div className="bg-gray-100 px-4 py-2 border-l-4 border-purple-400">
                              <h5 className="text-md font-semibold text-gray-800">
                                {module.moduleName} ({module.moduleWDCourseCode})
                              </h5>
                              <p className="text-xs text-gray-600">
                                Duration: {module.duration} hours
                              </p>
                              {module.description && (
                                <p className="text-xs text-gray-600 italic">{module.description}</p>
                              )}
                            </div>

                            <div className="overflow-x-auto">
                              <table className="w-full border-collapse min-w-[1000px]">
                                <thead>
                                  <tr className="bg-gray-200 text-gray-600">
                                    <th className="px-3 py-2 text-left font-semibold text-sm border-b w-32">Date</th>
                                    <th className="px-3 py-2 text-left font-semibold text-sm border-b w-20">Day</th>
                                    <th className="px-3 py-2 text-left font-semibold text-sm border-b w-24">Time</th>
                                    <th className="px-3 py-2 text-left font-semibold text-sm border-b w-32">Nomination Status</th>
                                    <th className="px-3 py-2 text-left font-semibold text-sm border-b w-24">Cancel</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {module.connects.map((connect, connectIdx) => {
                                    const userNomination = getUserNomination(connect);
                                    const nominationStatus = userNomination?.status || 'NOT_NOMINATED';
                                    
                                    return (
                                    <tr key={`${connect.connectId}-${connectIdx}`} className="hover:bg-gray-50 border-b border-gray-200">
                                      <td className="px-3 py-2 text-sm whitespace-nowrap">
                                        {format(new Date(connect.date), "dd-MMM-yyyy")}
                                      </td>
                                      <td className="px-3 py-2 text-sm">{connect.day}</td>
                                      <td className="px-3 py-2 text-sm">{connect.time}</td>
                                      <td className="px-3 py-2 text-sm">
                                        <span className={`px-2 py-1 rounded-full text-xs ${
                                          nominationStatus === "CONFIRMED" ? "bg-green-100 text-green-800" :
                                          nominationStatus === "CANCELLED" ? "bg-red-100 text-red-800" :
                                          nominationStatus === "OPTED_OUT" ? "bg-orange-100 text-orange-800" :
                                          nominationStatus === "NOMINATED" ? "bg-blue-100 text-blue-800" :
                                          nominationStatus === "NOT_NOMINATED" ? "bg-gray-100 text-gray-800" :
                                          "bg-yellow-100 text-yellow-800"
                                        }`}>
                                          {nominationStatus}
                                        </span>
                                      </td>
                                      <td className="px-3 py-2 text-sm">
                                        <input
                                          type="radio"
                                          name="cancelBlendedNomination"
                                          disabled={isConnectDisabled(connect.date, connect, userNomination)}
                                          checked={
                                            selectedConnect &&
                                            selectedConnect.connectId === connect.connectId &&
                                            selectedConnect.smeNominationId === userNomination?.smeNominationId
                                          }
                                          onChange={() => handleConnectSelection(connect, userNomination, program.blendedProgramOfferingId, module.moduleId)}
                                        />
                                      </td>
                                    </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {selectedConnect && selectedConnect.userNomination?.status === "NOMINATED" && (
            <div className="mb-4">
              <label className="block font-semibold mb-2">
                Reason for cancellation:
              </label>
              <textarea
                rows="3"
                value={reason || ""}
                onChange={handleReasonChange}
                placeholder="Enter reason here......"
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm"
              />
            </div>
          )}

          {selectedConnect && selectedConnect.userNomination?.status === "CONFIRMED" && (
            <div className="space-y-4 mb-4">
              <div>
                <label className="block font-semibold mb-2">
                  Reason for cancellation:
                </label>
                <textarea
                  rows="3"
                  value={reason || ""}
                  onChange={handleReasonChange}
                  placeholder="Enter reason here......"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm"
                />
              </div>
              <div>
                <label className="block font-semibold mb-2">
                  Alternative SME EmailID:
                </label>
                <input
                  type="email"
                  value={alternativeSME || ""}
                  onChange={handleAlternativeSMEChange}
                  placeholder="Enter alternative SME's enterprise id"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm"
                />
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-center">
            <button
              onClick={handleCancelNomination}
              className="bg-green-500 text-white px-6 py-2 rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
              disabled={isFrozen || !selectedConnect}
            >
              Request Nomination Cancellation
            </button>
          </div>
        </div>
      )}

      {selectedMonth && blendedNominations.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-600">No blended program nominations found for the selected month.</p>
        </div>
      )}
    </div>
  );
}

export default CheckBlendedSMENominationStatus;